import { useEffect, useRef, useState } from "react"
import { useSession } from "../auth/session"
import { useDispatch, useSelector } from "react-redux"
import Api from "../api/api"
import { fileToSquareJpeg, uploadAvatar } from "../utils/image"
import { updateProfileThunk } from "../redux/usersSlice"
import { meSelector } from "../redux/globalSlice"
import { AppDispatch } from "../redux/store"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Preview-and-confirm for a freshly picked avatar file. The image is cropped
// square and downscaled client-side, PUT to a presigned S3 URL, and the
// resulting CloudFront URL written to the Cognito picture attribute via
// updateProfileThunk (which refreshes the avatar everywhere in the app).
export default function AvatarUploadDialog({ file, onClose }: { file: File, onClose: () => void }) {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dispatch = useDispatch<AppDispatch>()
  const me = useSelector(meSelector)?.data
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"preparing" | "preview" | "saving" | "error" | "success">("preparing")
  const [errorMessage, setErrorMessage] = useState("")
  const [blob, setBlob] = useState<Blob | null>(null)
  const [previewUrl, setPreviewUrl] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  useEffect(() => {
    let cancelled = false
    fileToSquareJpeg(file)
      .then((jpeg) => {
        if (cancelled) return
        setBlob(jpeg)
        setPreviewUrl(URL.createObjectURL(jpeg))
        setStep("preview")
      })
      .catch(() => {
        if (cancelled) return
        setErrorMessage("That file doesn't look like an image.")
        setStep("error")
      })
    return () => { cancelled = true }
  }, [file])

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }
  }, [previewUrl])

  const handleSave = async () => {
    if (!blob || !me) return
    setStep("saving")
    setErrorMessage("")
    try {
      const { uploadUrl, imageUrl } = await Api.createAvatarUpload({ accessToken })
      await uploadAvatar(uploadUrl, blob)
      await dispatch(updateProfileThunk({ id: me.id, updates: { imageUrl }, accessToken })).unwrap()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  const busy = step === "preparing" || step === "saving"

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Profile Picture</h2>

        {step === "preparing" && (
          <p><Spinner size={16} /> Preparing image…</p>
        )}

        {step !== "preparing" && step !== "success" && (
          <>
            {previewUrl && (
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ width: 128, height: 128, borderRadius: "50%", objectFit: "cover" }}
                />
              </div>
            )}

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              {blob && (
                <button
                  className="btn-upgrade"
                  disabled={busy}
                  onClick={handleSave}
                >
                  {step === "saving" ? <><Spinner size={12} /> Saving…</> : "Save"}
                </button>
              )}
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">Profile picture updated.</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
